import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import {
  CallRecord,
  CallRecording,
  CallRecordUpdateEvent,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/message.pb';
import { InfiniteData } from 'react-query';
import { RecentCallRecords } from '@frontend/phone-panels';

type RecentCallRecordRow = RecentCallRecords['data'][number];
type RecentCallIntelPayload = NonNullable<CallRecordUpdateEvent['callIntelInsight']>;
type RecentCallIntelUpdate = RecentCallIntelPayload | Call | undefined;

const isRecentCallIntelPayload = (
  callIntelInsight: RecentCallIntelUpdate
): callIntelInsight is RecentCallIntelPayload => {
  return !!callIntelInsight && 'channelId' in callIntelInsight;
};

const getRecentCallIntelChannelId = (callIntelInsight: RecentCallIntelUpdate) => {
  if (!callIntelInsight) {
    return undefined;
  }

  if (isRecentCallIntelPayload(callIntelInsight)) {
    return callIntelInsight.channelId;
  }

  return (callIntelInsight as Call).id;
};

const mapRecentCallRecordPages = (
  callRecords: InfiniteData<RecentCallRecords>,
  updateRecord: (record: RecentCallRecordRow) => RecentCallRecordRow
) => {
  const updatedPages = callRecords.pages.map((page) => {
    const updatedData = page.data.map(updateRecord);

    return {
      ...page,
      data: updatedData,
      rows: updatedData,
    };
  });

  return {
    ...callRecords,
    pages: updatedPages,
  };
};

export const prependRecentCallRecord = (
  callRecords: InfiniteData<RecentCallRecords>,
  newRecord: RecentCallRecordRow
): InfiniteData<RecentCallRecords> => {
  const newData = { ...callRecords, pages: [...callRecords.pages] };

  if (newData.pages.length === 0) {
    newData.pages.push({
      data: [],
      rows: [],
      meta: {
        lastId: undefined,
        endDate: undefined,
        hasNext: false,
      },
    });
  }

  newData.pages[0] = {
    ...newData.pages[0],
    data: [newRecord, ...newData.pages[0].data],
    rows: [newRecord, ...newData.pages[0].rows],
  };

  return newData;
};

export const applyCallRecordingUpdate = (
  callRecords: InfiniteData<RecentCallRecords> | undefined,
  recordingInfo: CallRecording
) => {
  if (!callRecords) {
    return undefined;
  }

  const callRecordIndex =
    callRecords.pages[0]?.data.findIndex((record) => record.channelId === recordingInfo.channelId) ?? -1;

  if (callRecordIndex === -1) {
    return undefined;
  }

  const callRecord = callRecords.pages[0].data[callRecordIndex];
  const updatedCallRecord = {
    ...callRecord,
    isRecordingBeingProcessed: false,
    recordingFilename: recordingInfo.recordingFilename,
    recordingPath: recordingInfo.recordingPath,
  };

  const newPageData = [
    ...callRecords.pages[0].data.slice(0, callRecordIndex),
    updatedCallRecord,
    ...callRecords.pages[0].data.slice(callRecordIndex + 1),
  ];

  return {
    ...callRecords,
    pages: [
      {
        ...callRecords.pages[0],
        data: newPageData,
        rows: newPageData,
      },
      ...callRecords.pages.slice(1),
    ],
  };
};

export const applyCallRecordUpdate = (
  callRecords: InfiniteData<RecentCallRecords> | undefined,
  callRecord: CallRecord
) => {
  if (!callRecords || !callRecord.channelId) {
    return undefined;
  }

  return mapRecentCallRecordPages(callRecords, (record) => {
    if (record.channelId !== callRecord.channelId) {
      return record;
    }

    return {
      ...record,
      aiMetadata: callRecord.aiMetadata,
    };
  });
};

export const applyCallIntelInsightUpdate = (
  callRecords: InfiniteData<RecentCallRecords> | undefined,
  callIntelInsight: RecentCallIntelUpdate,
  channelId = getRecentCallIntelChannelId(callIntelInsight)
) => {
  if (!callRecords || !channelId) {
    return undefined;
  }

  return mapRecentCallRecordPages(callRecords, (record) => {
    if (record.channelId !== channelId) {
      return record;
    }

    return {
      ...record,
      callIntelInsights: callIntelInsight,
    };
  });
};

export const applyFollowupTaskCountUpdate = (
  callRecords: InfiniteData<RecentCallRecords> | undefined,
  params: CallRecordUpdateEvent['tasksCount']
) => {
  if (!callRecords || !params?.channelId) {
    return undefined;
  }

  return mapRecentCallRecordPages(callRecords, (record) => {
    if (record.channelId !== params.channelId) {
      return record;
    }

    return {
      ...record,
      taskCount: params.count ?? 0,
    };
  });
};

export const applyRecentCallTagsUpdate = ({
  callRecords,
  channelId,
  tagIds,
  mappedTags,
}: {
  callRecords: InfiniteData<RecentCallRecords> | undefined;
  channelId: string;
  tagIds: string[];
  mappedTags: Record<string, Tag>;
}) => {
  if (!callRecords) {
    return undefined;
  }

  return mapRecentCallRecordPages(callRecords, (record) => {
    if (record.channelId !== channelId) {
      return record;
    }

    return {
      ...record,
      tagIds,
      tags: tagIds.map((tagId) => mappedTags[tagId]).filter(Boolean),
    };
  });
};
